/** Public page /pay/:token: pay an invoice online (anyone with the link). D-133: the secure page
 * of the provider chosen by the agency (CinetPay by default, then its fallbacks), named from the
 * API, never written here; the status shown is always the one read back from the provider by the
 * server, never the browser's return. A direct MTN / Orange request to a phone stays available
 * when the agency turns it on. */

import {
  ApiError,
  type MobileMethodEnum,
  type PublicOnlineFallback,
  usePublicPayCreate,
  usePublicPayOnlineCreate,
  usePublicPayOnlineNotifyRestoredCreate,
  usePublicPayOnlineRetrieve,
  usePublicPayOnlineSimulateCreate,
  usePublicPayRetrieve,
} from "@agencyos/api-client";
import { formatXaf, localText, withPaymentFee } from "@agencyos/core";
import { Backdrop, Badge, Button, GlassCard, Skeleton, TextField } from "@agencyos/ui-web";
import { CheckCircle2, CircleAlert, FileText, FlaskConical, Loader2, Mail, Smartphone, Wallet } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { CountrySelect } from "../academy/sales/CountrySelect";
import { LanguageSwitcher, ThemeSwitcher } from "../components/Preferences";
import { fieldErrors } from "../lib/settings";
import { amountOf, dateText } from "./billing/shared";

const MOBILE_METHODS: MobileMethodEnum[] = ["momo_mtn", "momo_orange"];

export function PayPage() {
  const { t, i18n } = useTranslation();
  const { token = "" } = useParams();
  const [waiting, setWaiting] = useState(false);
  const [search, setSearch] = useSearchParams();
  const paymentId = search.get("payment") ?? "";
  // D-131: the payer's country of residence decides the provider's fee (none in the EU/EEA)
  const [country, setCountry] = useState("");
  const invoice = usePublicPayRetrieve(token, country ? { country } : undefined, {
    query: {
      enabled: Boolean(token),
      retry: false,
      refetchInterval: waiting ? 15000 : false,
      placeholderData: (previous) => previous,
    },
  });
  const pay = usePublicPayCreate();
  const data = invoice.data?.status === 200 ? invoice.data.data : undefined;
  const methods = (data?.methods ?? []).filter((m): m is MobileMethodEnum => (MOBILE_METHODS as string[]).includes(m));
  // D-133: the primary provider chosen by the agency; D-132: its fallback while it is unavailable
  const hasOnline = Boolean(data?.online_provider);

  // D-128: the online payment started from this page, read back every few seconds
  const startOnline = usePublicPayOnlineCreate();
  const notify = usePublicPayOnlineNotifyRestoredCreate();
  const [offered, setOffered] = useState<PublicOnlineFallback | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const simulateOnline = usePublicPayOnlineSimulateCreate();
  const online = usePublicPayOnlineRetrieve(token, paymentId, {
    query: {
      enabled: Boolean(token && paymentId),
      retry: false,
      refetchInterval: (query) => {
        const current = query.state.data;
        return current?.status === 200 && current.data.status === "pending" ? 4000 : false;
      },
    },
  });
  const onlineState = online.data?.status === 200 ? online.data.data : undefined;
  const onlineProvider = onlineState?.provider_label.split(" (")[0] ?? "";
  const refetchInvoice = invoice.refetch;
  useEffect(() => {
    if (onlineState?.status === "confirmed") void refetchInvoice();
  }, [onlineState?.status, refetchInvoice]);

  const [method, setMethod] = useState<MobileMethodEnum | "">("");
  const [phone, setPhone] = useState("");
  const [payer, setPayer] = useState("");
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const errors = fieldErrors(pay.error);
  const startError =
    startOnline.error instanceof ApiError && [409, 503].includes(startOnline.error.status) ? null : startOnline.error;
  const onlineErrors = fieldErrors(startError);
  const detail = pay.error instanceof ApiError ? pay.error.detail : undefined;
  const chosen = method || methods[0] || "";
  const balance = data?.balance_xaf ?? 0;
  const value = amount ? amountOf(amount) : balance;
  const fee = offered?.fee ?? data?.online_fee;
  const providerKey = offered?.provider ?? data?.online_provider ?? "";
  const providerName = offered?.provider_name ?? data?.online_name ?? "";
  const fallbackName = offered?.provider_name || data?.maintenance.fallback || "";
  const noOnlineLeft = Boolean(data?.maintenance.notify) || unavailable;
  const feeLabel = fee ? localText(fee.label as Record<string, string>, i18n.resolvedLanguage ?? "fr") : "";
  const charged = withPaymentFee(value > 0 && value <= balance ? value : 0, fee);

  const checkAmount = () => {
    if (!(value > 0) || value > balance) {
      setAmountError(t("pay.amountInvalid", { balance: formatXaf(balance) }));
      return false;
    }
    setAmountError("");
    return true;
  };

  const payOnline = () => {
    if (!checkAmount()) return;
    startOnline.mutate(
      {
        token,
        data: {
          amount_xaf: value,
          ...(data?.country ? { country: data.country } : {}),
          ...(providerKey ? { provider: providerKey } : {}),
          ...(payer.trim() ? { payer_name: payer.trim() } : {}),
          ...(email.trim() ? { email: email.trim() } : {}),
        },
      },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          // the simulator (test mode without a key) stays on this page; the provider's page replaces it
          if (response.data.simulated) setSearch({ payment: response.data.payment }, { replace: true });
          else window.location.assign(response.data.redirect_url);
        },
        onError: (failure) => {
          // D-132: the fallback has another fee: shown (with its total) before the payer confirms
          if (failure instanceof ApiError && failure.status === 409) setOffered(failure.body as PublicOnlineFallback);
          else if (failure instanceof ApiError && failure.status === 503) setUnavailable(true);
        },
      },
    );
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!chosen || !checkAmount()) return;
    pay.mutate(
      {
        token,
        data: { method: chosen, phone: phone.trim(), amount_xaf: value, ...(payer.trim() ? { payer_name: payer.trim() } : {}) },
      },
      { onSuccess: (response) => response.status === 201 && setWaiting(true) },
    );
  };

  const cancelled = data?.status === "cancelled";
  const paid = !cancelled && (data?.status === "paid" || (data !== undefined && balance === 0));

  const payerFields = (
    <>
      <TextField
        label={t("pay.payer")}
        hint={t("pay.payerHint")}
        autoComplete="name"
        value={payer}
        maxLength={150}
        error={errors.payer_name || onlineErrors.payer_name}
        onChange={(e) => setPayer(e.target.value)}
      />
      <TextField
        label={t("pay.amount")}
        hint={t("pay.amountHint", { balance: formatXaf(balance) })}
        inputMode="numeric"
        value={formatXaf(value || 0, { suffix: false })}
        error={amountError || errors.amount_xaf || onlineErrors.amount_xaf}
        onChange={(e) => setAmount(e.target.value.replace(/\D/g, "") || "0")}
      />
    </>
  );

  return (
    <>
      <Backdrop intensity="vivid" />
      <main className="login pay-page">
        <div className="login-tools">
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
        <GlassCard level="thick" className="login-card pay-card stack">
          {invoice.isPending ? (
            <>
              <Skeleton style={{ height: 24, width: "60%" }} />
              <Skeleton style={{ height: 120 }} />
            </>
          ) : !data ? (
            <div role="alert" className="stack">
              <h1>{t("pay.invalidTitle")}</h1>
              <p className="muted">{t("pay.invalid")}</p>
            </div>
          ) : (
            <>
              <div className="stack pay-head">
                <p className="login-brand">{data.agency}</p>
                <h1>{t("pay.title", { number: data.number })}</h1>
                <p className="muted">{t("pay.hello", { name: data.client })}</p>
              </div>
              <div className="pay-balance">
                <span className="muted">{paid ? t("pay.total") : t("pay.balance")}</span>
                <span className="big-number">{formatXaf(paid ? data.total_ttc_xaf : balance)}</span>
                {data.due_date && !paid && !cancelled ? <span className="muted">{t("pay.due", { date: dateText(data.due_date, i18n.language) })}</span> : null}
              </div>
              {data.document_url ? (
                <a className="link" href={data.document_url} target="_blank" rel="noreferrer">
                  <FileText size={16} aria-hidden="true" /> {t("pay.pdf")}
                </a>
              ) : null}

              {onlineState ? (
                <div role="status" aria-live="polite" className="stack pay-done">
                  {onlineState.status === "confirmed" ? (
                    <CheckCircle2 size={32} aria-hidden="true" className="ok" />
                  ) : onlineState.status === "pending" ? (
                    <Loader2 size={32} aria-hidden="true" className="as-spin" />
                  ) : (
                    <CircleAlert size={32} aria-hidden="true" className="ko" />
                  )}
                  <strong>
                    {onlineState.status === "confirmed"
                      ? t("pay.online.confirmedTitle")
                      : onlineState.status === "pending"
                        ? t("pay.online.pendingTitle")
                        : t("pay.online.failedTitle")}
                  </strong>
                  <span className="muted">
                    {onlineState.status === "confirmed"
                      ? t("pay.online.confirmedText", { number: onlineState.receipt_number })
                      : onlineState.status === "pending"
                        ? t("pay.online.pendingText", { provider: onlineProvider })
                        : t("pay.online.failedText")}
                  </span>
                  <span>{formatXaf(onlineState.total_xaf)}</span>
                  {onlineState.fee_xaf > 0 ? (
                    <span className="muted">
                      {t("pay.online.feeSplit", { amount: formatXaf(onlineState.amount_xaf), fee: formatXaf(onlineState.fee_xaf) })}
                    </span>
                  ) : null}
                  {onlineState.channel ? <span className="muted">{t("pay.online.channel", { channel: onlineState.channel })}</span> : null}
                  {onlineState.failover_from ? (
                    <span className="muted">
                      {t("pay.online.failover", { provider: onlineProvider, from: onlineState.failover_from })}
                    </span>
                  ) : null}
                  {onlineState.status === "pending" && onlineState.simulated ? (
                    <section className="stack" aria-labelledby="pay-sandbox-title">
                      <strong id="pay-sandbox-title">
                        <FlaskConical aria-hidden size={18} /> {t("pay.online.sandboxTitle")}
                      </strong>
                      <span className="muted">{t("pay.online.sandboxText", { provider: onlineProvider })}</span>
                      <div className="row">
                        <Button
                          disabled={simulateOnline.isPending}
                          onClick={() =>
                            simulateOnline.mutate({ token, paymentId, data: { success: true } }, { onSettled: () => void online.refetch() })
                          }
                        >
                          {t("pay.online.simulateOk")}
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={simulateOnline.isPending}
                          onClick={() =>
                            simulateOnline.mutate({ token, paymentId, data: { success: false } }, { onSettled: () => void online.refetch() })
                          }
                        >
                          {t("pay.online.simulateKo")}
                        </Button>
                      </div>
                    </section>
                  ) : null}
                  {onlineState.is_test ? <Badge tone="warning">{t("pay.online.test")}</Badge> : null}
                  {onlineState.status !== "pending" && !paid ? (
                    <Button variant="secondary" onClick={() => setSearch({}, { replace: true })}>
                      {t("pay.again")}
                    </Button>
                  ) : null}
                </div>
              ) : paid ? (
                <div role="status" className="stack pay-done">
                  <CheckCircle2 size={32} aria-hidden="true" className="ok" />
                  <strong>{t("pay.paid")}</strong>
                  <span className="muted">{t("pay.paidText")}</span>
                </div>
              ) : cancelled ? (
                <p role="note" className="ko">
                  {t("pay.cancelled")}
                </p>
              ) : noOnlineLeft && !methods.length ? (
                <div role="alert" className="notice-warning billing-notice stack">
                  <strong>
                    <CircleAlert aria-hidden size={16} /> {t("pay.online.unavailableTitle")}
                  </strong>
                  <span>{t("pay.online.unavailable")}</span>
                  {notify.isSuccess && notify.data.status === 201 ? (
                    <span role="status">{t("pay.online.notifyDone", { email: notify.data.data.email })}</span>
                  ) : (
                    <>
                      <TextField
                        label={t("pay.online.notifyEmail")}
                        type="email"
                        autoComplete="email"
                        value={notifyEmail}
                        maxLength={254}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                      />
                      <Button
                        variant="secondary"
                        disabled={notify.isPending || !notifyEmail.trim()}
                        onClick={() => notify.mutate({ token, data: { email: notifyEmail.trim() } })}
                        icon={<Mail aria-hidden size={18} />}
                      >
                        {t("pay.online.notifyMe")}
                      </Button>
                    </>
                  )}
                </div>
              ) : !methods.length && !hasOnline ? (
                <div role="note" className="notice-warning billing-notice">
                  <strong>{t("pay.noMethodTitle")}</strong>
                  <span>{t("pay.noMethod")}</span>
                </div>
              ) : waiting && pay.isSuccess ? (
                <div role="status" className="stack pay-done">
                  <Smartphone size={32} aria-hidden="true" />
                  <strong>{t("pay.confirmTitle")}</strong>
                  <span>{t("pay.confirmText", { amount: formatXaf(value) })}</span>
                  <span className="muted">{t("pay.confirmWait")}</span>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setWaiting(false);
                      pay.reset();
                    }}
                  >
                    {t("pay.again")}
                  </Button>
                </div>
              ) : (
                <div className="stack">
                  {hasOnline ? (
                    <div className="stack">
                      {payerFields}
                      {data.email_needed ? (
                        <TextField
                          label={t("pay.online.email")}
                          hint={t("pay.online.emailHint", { provider: providerName })}
                          type="email"
                          autoComplete="email"
                          value={email}
                          maxLength={254}
                          error={onlineErrors.email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      ) : null}
                      <CountrySelect
                        label={t("pay.online.country")}
                        hint={t("pay.online.countryHint")}
                        value={country || data.country}
                        onChange={(next) => {
                          setCountry(next);
                          setOffered(null);
                        }}
                      />
                      {fallbackName ? (
                        <p role="status" className="notice-warning">
                          {t("pay.online.fallbackNotice", { provider: fallbackName })}
                          {offered ? ` ${t("pay.online.fallbackTotal", { provider: fallbackName, total: formatXaf(charged.total) })}` : ""}
                        </p>
                      ) : null}
                      {/* D-131: the provider's fee on its own line, with the total, BEFORE payment */}
                      {feeLabel ? (
                        <dl className="pay-fees" aria-live="polite">
                          <div>
                            <dt>{t("pay.online.forInvoice")}</dt>
                            <dd className="ao-num">{formatXaf(charged.amount)}</dd>
                          </div>
                          <div>
                            <dt>{feeLabel}</dt>
                            <dd className="ao-num">{formatXaf(charged.fee)}</dd>
                          </div>
                          <div className="pay-fees__total">
                            <dt>{t("pay.online.totalTtc")}</dt>
                            <dd className="ao-num">{formatXaf(charged.total)}</dd>
                          </div>
                          {fee?.exempt ? (
                            <p className="muted">{fee.reason === "eu_eea" ? t("pay.online.feeNoneEu") : t("pay.online.feeNone")}</p>
                          ) : null}
                        </dl>
                      ) : null}
                      <Button
                        block
                        onClick={payOnline}
                        disabled={startOnline.isPending || (data.email_needed && !email.trim())}
                        icon={<Wallet aria-hidden size={18} />}
                      >
                        {startOnline.isPending
                          ? t("pay.online.starting")
                          : providerName
                            ? t("pay.online.chooseWith", { provider: providerName })
                            : t("pay.online.choose")}
                      </Button>
                      <p className="muted pay-note">
                        <Badge>{t("pay.secure")}</Badge> {t("pay.online.hint", { provider: providerName })}
                      </p>
                      {unavailable ? (
                        <p role="alert" className="ko">
                          {t("pay.online.unavailable")}
                        </p>
                      ) : null}
                      {startError && !Object.keys(onlineErrors).length ? (
                        <p role="alert" className="ko">
                          {t("pay.online.failed")}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {methods.length ? (
                    <form className="stack" onSubmit={onSubmit} noValidate>
                      {hasOnline ? <p className="muted pay-note">{t("pay.online.or")}</p> : null}
                      <fieldset className="plain-fieldset stack">
                        <legend className="pay-legend">{t("pay.method")}</legend>
                        <div className="pay-methods">
                          {methods.map((m) => (
                            <label key={m} className={`pay-method${chosen === m ? " pay-method--on" : ""}`}>
                              <input type="radio" name="method" value={m} checked={chosen === m} onChange={() => setMethod(m)} />
                              <span>{t(`pay.methods.${m}`)}</span>
                            </label>
                          ))}
                        </div>
                        {errors.method ? <p className="ao-field__error">{errors.method}</p> : null}
                      </fieldset>
                      <TextField
                        label={t("pay.phone")}
                        hint={t("pay.phoneHint")}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={phone}
                        maxLength={30}
                        error={errors.phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                      {hasOnline ? null : payerFields}
                      {detail ? (
                        <p role="alert" className="ko">
                          {detail}
                        </p>
                      ) : null}
                      {pay.isError && !detail && !Object.keys(errors).length ? (
                        <p role="alert" className="ko">
                          {t("pay.failed")}
                        </p>
                      ) : null}
                      <Button type="submit" block variant={hasOnline ? "secondary" : "primary"} disabled={pay.isPending || !phone.trim()}>
                        {pay.isPending ? t("pay.sending") : t("pay.submit", { amount: formatXaf(value || 0) })}
                      </Button>
                      <p className="muted pay-note">
                        <Badge>{t("pay.secure")}</Badge> {t("pay.note")}
                      </p>
                    </form>
                  ) : null}
                </div>
              )}
            </>
          )}
        </GlassCard>
      </main>
    </>
  );
}
